import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { growthSdk, type Subscription } from '@fff-web/growth-sdk';
import GlobalNavInternalContext from '../context/GlobalNavInternalContext';
import { getCoreBoxState, isTrialSub, type CoreBoxState } from '../helpers/coreBoxState';

export type SubscriptionStatus = 'EXPIRED' | 'ACTIVE' | 'CANCELED';

export const useUserSubscriptions = () => {
  const { env } = useContext(GlobalNavInternalContext);

  const growthSdkInstance = useMemo(() => {
    return growthSdk(env.REACT_APP_ENVIRONMENT as Parameters<typeof growthSdk>[0], true);
  }, [env.REACT_APP_ENVIRONMENT]);

  const [clubMembershipSubscription, setClubMembershipSubscription] = useState<Subscription>();

  const [boxSubscriptions, setBoxSubscriptions] = useState<Subscription[]>();

  const [coreBoxState, setCoreBoxState] = useState<CoreBoxState>();

  // Matches the Asado navbar's club-member precedence: only an active MEMBERSHIP subscription can
  // supply the Growth-backed member-since date. Non-club members fall back to the account-level
  // `activationDate` (and then membership flags) in MyAccountPage.
  // `null` means an active club membership exists but Growth did not provide its startAt. Asado
  // does not fall through to the account date in that case; `undefined` means no active club sub.
  const [memberSince, setMemberSince] = useState<string | null>();

  // True until the first growth read settles; refetches don't reset it.
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserSubscriptions = useCallback(async () => {
    const response = await growthSdkInstance.getUserSubscriptionsV2();

    if (!response.ok) return;

    // `subscriptions` is optional on the derived wire type: growth omits it rather than
    // returning an empty array when the caller has none.
    const subscriptions = response.data.subscriptions ?? [];

    setCoreBoxState(getCoreBoxState(subscriptions));

    // One lookup for both values: two independent `find`s could resolve to different records on
    // the same render. Trials are excluded — growth v2 projects one into a MEMBERSHIP record that
    // is not a club membership, and reading it here would surface the trial's `startAt` as
    // member-since and its status as the nav badge.
    const clubMembershipSubs = subscriptions.filter(
      (sub) => sub.type === 'MEMBERSHIP' && !isTrialSub(sub),
    );
    const activeClubMembershipSub = clubMembershipSubs.find((sub) => sub.active);
    setMemberSince(activeClubMembershipSub ? (activeClubMembershipSub.startAt ?? null) : undefined);

    const clubMembershipSub = activeClubMembershipSub ?? clubMembershipSubs[0];

    // TODO: refactor properly to handle case for multiple BOX subscriptions
    const allBoxSubs = subscriptions.filter((sub) => sub.type === 'BOX');
    // Need to select the active one if it exists because a user can have multiple BOX subscriptions with various statuses
    const activeBoxSubs = allBoxSubs.filter((sub) => sub.status === 'ACTIVE');
    const boxSubs = activeBoxSubs?.length
      ? activeBoxSubs
      : allBoxSubs.length
        ? allBoxSubs
        : undefined;

    if (!clubMembershipSub && !boxSubs) return;

    if (clubMembershipSub) {
      setClubMembershipSubscription({
        ...clubMembershipSub,
        status: (clubMembershipSub.status === 'NON_RENEWING'
          ? 'CANCELED'
          : clubMembershipSub.status) as SubscriptionStatus,
      });
    }

    if (boxSubs) {
      setBoxSubscriptions(
        boxSubs.map((sub) => ({
          ...sub,
          status: (sub.status === 'NON_RENEWING' ? 'CANCELED' : sub.status) as SubscriptionStatus,
        })),
      );
    }
  }, [growthSdkInstance]);

  useEffect(() => {
    fetchUserSubscriptions().finally(() => setIsLoading(false));
  }, [fetchUserSubscriptions]);

  return {
    clubMembershipSubscription,
    boxSubscriptions,
    coreBoxState,
    memberSince,
    isLoading,
    refetch: fetchUserSubscriptions,
  };
};
